import {useCallback, useEffect, useMemo, useRef} from 'react';
import {
    constrainChatPanelWidth,
    DEFAULT_SIDEBAR_WIDTH,
    DEFAULT_THREAD_WIDTH,
    storedChatPanelWidth,
} from '../chatLayout';

const SIDEBAR_WIDTH_KEY = 'cos_chat_sidebar_width_v1';
const THREAD_WIDTH_KEY = 'cos_chat_thread_width_v1';

export default function useResizableChatLayout({hasThread, hasSidebar}) {
    const containerRef = useRef(null);
    const widthsRef = useRef({
        sidebar: storedChatPanelWidth(SIDEBAR_WIDTH_KEY, DEFAULT_SIDEBAR_WIDTH),
        thread: storedChatPanelWidth(THREAD_WIDTH_KEY, DEFAULT_THREAD_WIDTH),
    });
    const layoutStateRef = useRef({hasThread, hasSidebar});
    layoutStateRef.current = {hasThread, hasSidebar};

    const applyWidth = useCallback((panel, requestedWidth, persist = false) => {
        const container = containerRef.current;
        if (!container) return;
        const layoutState = layoutStateRef.current;
        const value = constrainChatPanelWidth(
            panel,
            requestedWidth,
            container.getBoundingClientRect().width,
            widthsRef.current,
            layoutState.hasThread,
            layoutState.hasSidebar,
        );
        widthsRef.current[panel] = value;
        container.style.setProperty(`--chat-${panel}-width`, `${value}px`);
        if (persist) {
            localStorage.setItem(panel === 'sidebar' ? SIDEBAR_WIDTH_KEY : THREAD_WIDTH_KEY, String(value));
        }
    }, []);

    const startResize = useCallback((panel, event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        const handle = event.currentTarget;
        const startX = event.clientX;
        const startWidth = widthsRef.current[panel];
        handle.setPointerCapture(event.pointerId);
        containerRef.current?.classList.add('is-resizing');

        const move = moveEvent => {
            const distance = moveEvent.clientX - startX;
            applyWidth(panel, startWidth + (panel === 'sidebar' ? distance : -distance));
        };
        const finish = finishEvent => {
            if (handle.hasPointerCapture(finishEvent.pointerId)) handle.releasePointerCapture(finishEvent.pointerId);
            handle.removeEventListener('pointermove', move);
            handle.removeEventListener('pointerup', finish);
            handle.removeEventListener('pointercancel', finish);
            containerRef.current?.classList.remove('is-resizing');
            applyWidth(panel, widthsRef.current[panel], true);
        };
        handle.addEventListener('pointermove', move);
        handle.addEventListener('pointerup', finish);
        handle.addEventListener('pointercancel', finish);
    }, [applyWidth]);

    const resizeWithKeyboard = useCallback((panel, event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const delta = (panel === 'sidebar' ? direction : -direction) * (event.shiftKey ? 50 : 10);
        applyWidth(panel, widthsRef.current[panel] + delta, true);
    }, [applyWidth]);

    const resetWidth = useCallback(panel => {
        applyWidth(panel, panel === 'sidebar' ? DEFAULT_SIDEBAR_WIDTH : DEFAULT_THREAD_WIDTH, true);
    }, [applyWidth]);

    useEffect(() => {
        const constrainPanels = () => {
            if (hasSidebar) applyWidth('sidebar', widthsRef.current.sidebar);
            if (hasThread) applyWidth('thread', widthsRef.current.thread);
        };
        constrainPanels();
        window.addEventListener('resize', constrainPanels);
        return () => window.removeEventListener('resize', constrainPanels);
    }, [applyWidth, hasSidebar, hasThread]);

    const style = useMemo(() => ({
        '--chat-sidebar-width': `${widthsRef.current.sidebar}px`,
        '--chat-thread-width': `${widthsRef.current.thread}px`,
    }), []);

    return {containerRef, style, startResize, resizeWithKeyboard, resetWidth};
}
